import _isNil from 'lodash/isNil';
import i18n from 'i18n';

import {
  LayerState,
  LoadHeightState,
  PalletDataState,
  UnitDimensionsState,
  UnitWeightState,
} from 'interfaces/store';
import {
  UnitDimension,
  UnitPackaging,
  UnitWeight,
} from 'enums/calculator';
import {
  DIMENSION_MAX_WIDTH_INCHES,
  DIMENSION_MAX_WIDTH_CM,
  WEIGHT_MAX_KG,
  WEIGHT_MAX_LB,
  BAGS_WEIGHT_MAX_KG,
  BAGS_WEIGHT_MAX_LB,
  PALLET_MAX_WIDTH_CM,
  PALLET_MAX_WIDTH_INCHES,
  PALLET_MAX_HEIGHT_CM,
  PALLET_MAX_HEIGHT_INCHES,
  MAX_LOAD_HEIGHT_INCHES,
  MAX_LOAD_HEIGHT_CM,
  DIMENSION_MIN_WIDTH_CM,
  DIMENSION_MIN_WIDTH_INCHES,
  PALLET_MIN_WIDTH_CM,
  PALLET_MIN_WIDTH_INCHES,
  DIMENSION_MIN_BAGS_HEIGHT_INCHES,
  DIMENSION_MIN_BAGS_HEIGHT_CM, MAX_OVERLAP_CM, MAX_OVERLAP_INCHES,
  CONVERSION_RATE,
} from 'config/calculator';
import { convertTo, roundNumber } from 'utils/numbers';

const check = (value: number, max: number, unit?: string, field?: string, message?: string) => {
  if (value > max) {
    let validationMessage = '';
    if (message) {
      validationMessage = message;
    } else {
      validationMessage = `The ${field} cannot exceed ${max}${unit && unit.toLowerCase()}`;
    }
    return i18n.t(validationMessage);
  }
  return null;
};

const checkMin = (value: number, min: number, unit: string, field: string) => {
  if (value < min) {
    const message = `The ${field} cannot be lower than ${min}${unit.toLowerCase()}`;
    return i18n.t(message);
  }
  return null;
};

export const validateDimensions = (dimensions: UnitDimensionsState, packaging: UnitPackaging) => {
  const {
    type,
    width,
    length,
    height,
  } = dimensions;

  let newWidth = width;
  let newLength = length;
  let newHeight = height;
  let widthError;
  let lengthError;
  let heightError;
  const max = type === UnitDimension.CM ? DIMENSION_MAX_WIDTH_CM : DIMENSION_MAX_WIDTH_INCHES;
  const min = type === UnitDimension.CM ? DIMENSION_MIN_WIDTH_CM : DIMENSION_MIN_WIDTH_INCHES;
  let minHeight = min;

  // If bags packaging, change minimum height.
  if (packaging === UnitPackaging.BAG) {
    minHeight = type === UnitDimension.CM
      ? DIMENSION_MIN_BAGS_HEIGHT_CM
      : DIMENSION_MIN_BAGS_HEIGHT_INCHES;
  }

  if (!_isNil(width)) {
    widthError = check(width, max, type, 'width') || checkMin(width, min, type, 'width');
    newWidth = roundNumber(width);
  }
  if (!_isNil(length)) {
    lengthError = check(length, max, type, 'length') || checkMin(length, min, type, 'length');
    newLength = roundNumber(length);
  }
  if (!_isNil(height)) {
    heightError = check(height, max, type, 'height') || checkMin(height, minHeight, type, 'height');
    newHeight = roundNumber(height);
  }

  return {
    ...dimensions,
    ...{
      width: newWidth,
      length: newLength,
      height: newHeight,
      widthError,
      lengthError,
      heightError,
    },
  };
};

export const validateWeight = (weight: UnitWeightState, packaging: UnitPackaging) => {
  const {
    type,
    value,
  } = weight;

  let newValue = value;
  let error;
  let max;

  if (!_isNil(value)) {
    if (packaging === UnitPackaging.BOX) {
      max = type === UnitWeight.KG ? WEIGHT_MAX_KG : WEIGHT_MAX_LB;
    } else {
      // Applies for cement and pillow (overlapping) bags.
      max = type === UnitWeight.KG ? BAGS_WEIGHT_MAX_KG : BAGS_WEIGHT_MAX_LB;
    }
    error = check(value, max, type, 'weight');
    newValue = roundNumber(value);
  }

  return { ...weight, ...{ value: newValue, error } };
};

export const validateHeight = (
  height: LoadHeightState,
  dimensions: UnitDimensionsState,
  palletData: PalletDataState,
) => {
  const {
    unit,
    value,
  } = height;

  const {
    height: packageHeight,
    type: packageUnit,
  } = dimensions;

  let newValue = value;
  let warning;

  if (!_isNil(value)) {
    // Maximum height in cm or inches.
    let maxHeight = unit === UnitDimension.CM ? MAX_LOAD_HEIGHT_CM : MAX_LOAD_HEIGHT_INCHES;
    let packageHeightValue = packageHeight;
    if (packageUnit !== unit) {
      packageHeightValue = convertTo(Number(packageHeight), height.unit);
    }

    if (palletData.height) {
      if (unit === UnitDimension.CM) {
        const palletHeight = palletData.unitType === UnitDimension.INCH
          ? Math.floor(Number(palletData.height) * CONVERSION_RATE)
          : Math.floor(Number(palletData.height));
        maxHeight -= Math.floor(Number(palletHeight));
      } else {
        const palletHeight = palletData.unitType === UnitDimension.CM
          ? Math.floor(Number(palletData.height) / CONVERSION_RATE)
          : Math.floor(Number(palletData.height));
        maxHeight -= Number(palletHeight);
      }
    }

    // Error validation message.
    const validationErrMessage = `The total load height including the pallet cannot exceed ${unit === UnitDimension.CM ? MAX_LOAD_HEIGHT_CM : MAX_LOAD_HEIGHT_INCHES} ${unit.toLowerCase()}.`;

    /* Validate maximum height */
    warning = check(
      value,
      maxHeight,
      undefined,
      undefined,
      validationErrMessage,
    );

    if (value < Number(packageHeightValue)) {
      warning = 'The total load can\'t be lower than the package load.';
    }

    newValue = roundNumber(value);
  }

  return { ...height, ...{ value: newValue, warning } };
};

export const validatePalletData = (pallet: PalletDataState) => {
  const {
    unitType,
    width,
    length,
    height,
  } = pallet;

  let newWidth = width;
  let newLength = length;
  let newHeight = height;
  let widthError;
  let lengthError;
  let heightError;
  const max = unitType === UnitDimension.CM ? PALLET_MAX_WIDTH_CM : PALLET_MAX_WIDTH_INCHES;
  const min = unitType === UnitDimension.CM ? PALLET_MIN_WIDTH_CM : PALLET_MIN_WIDTH_INCHES;
  const maxHeight = unitType === UnitDimension.CM ? PALLET_MAX_HEIGHT_CM : PALLET_MAX_HEIGHT_INCHES;

  if (!_isNil(width)) {
    widthError = check(width, max, unitType, 'width') || checkMin(width, min, unitType, 'width');
    newWidth = roundNumber(width);
  }
  if (!_isNil(length)) {
    lengthError = check(length, max, unitType, 'length') || checkMin(length, min, unitType, 'length');
    newLength = roundNumber(length);
  }
  if (!_isNil(height)) {
    heightError = check(height, maxHeight, unitType, 'height');
    newHeight = roundNumber(height);
  }

  return {
    ...pallet,
    ...{
      width: newWidth,
      length: newLength,
      height: newHeight,
      widthError,
      lengthError,
      heightError,
    },
  };
};

export const validateOverlap = (layer: LayerState, unitType: UnitDimension) => {
  const {
    horizontalOverlap,
    verticalOverlap,
  } = layer;

  let newVerticalOverlap = verticalOverlap;
  let newHorizontalOverlap = horizontalOverlap;
  let verticalOverlapError;
  let horizontalOverlapError;
  const max = unitType === UnitDimension.CM ? MAX_OVERLAP_CM : MAX_OVERLAP_INCHES;

  if (!_isNil(horizontalOverlap)) {
    horizontalOverlapError = check(horizontalOverlap, max, unitType, 'horizontal overlap') || checkMin(horizontalOverlap, 0, unitType, 'horizontal overlap');
    newHorizontalOverlap = roundNumber(horizontalOverlap);
  }
  if (!_isNil(verticalOverlap)) {
    verticalOverlapError = check(verticalOverlap, max, unitType, 'vertical overlap') || checkMin(verticalOverlap, 0, unitType, 'vertical overlap');
    newVerticalOverlap = roundNumber(verticalOverlap);
  }

  return {
    ...layer,
    ...{
      verticalOverlap: newVerticalOverlap,
      horizontalOverlap: newHorizontalOverlap,
      verticalOverlapError,
      horizontalOverlapError,
    },
  };
};
